import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../../utils/apiService";
import MessageHeaderLayer from "./MessageHeaderLayer";
import CodeSamplesPanel from "./CodeSamplesPanel";
import "./ApiTestPage.css";

const LocationLayer = () => {
  const [alertMessage, setAlertMessage] = useState("");
  const [spinner, setSpinner] = useState(false);
  const [data, setData] = useState({
    client_id: null,
    token: null,
    mobile: "",
    latitude: "",
    longitude: "",
    text: "",
    type: "location",
  });

  const hideAlert = (sec = 1) => {
    setTimeout(() => {
      setAlertMessage("");
      setSpinner(false);
    }, sec * 1000);
  };

  const validated = () => {
    if (!data.client_id) return { isValid: false, messageText: "Instance is required!" };
    if (!data.token) return { isValid: false, messageText: "Token is required!" };
    if (!data.mobile) return { isValid: false, messageText: "Phone is required!" };
    if (!data.latitude) return { isValid: false, messageText: "Latitude is required!" };
    if (!data.longitude) return { isValid: false, messageText: "Longitude is required!" };
    return { isValid: true, messageText: "" };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { isValid, messageText } = validated();
    if (!isValid) {
      setAlertMessage({ message: messageText, class: "danger" });
      hideAlert(2);
      return;
    }

    setSpinner(true);
    try {
      const formData = new FormData();
      for (let key in data) {
        if (data[key] != null) formData.append(key, data[key]);
      }
      const response = await apiCall({
        url: "send-message/send-location",
        method: "POST",
        body: formData,
        withoutStringify: true,
      });

      setAlertMessage({
        message: response.msg,
        class: response.success ? "success" : "danger",
      });
      hideAlert(2);
    } catch (error) {
      setAlertMessage({ message: error.message, class: "danger" });
      hideAlert(2);
    }
  };

  useEffect(() => {
    document.title = "Send Location";
  }, []);

  const sampleParams = useMemo(
    () => ({
      clientId: data.client_id,
      token: data.token,
      mobile: data.mobile,
      latitude: data.latitude,
      longitude: data.longitude,
      message: data.text,
    }),
    [data.client_id, data.token, data.mobile, data.latitude, data.longitude, data.text]
  );

  return (
    <div className="api-test-page">
      <div className="card">
        <div className="card-body">
          <div className="api-test-welcome">
            <div>
              <h5>Location message test</h5>
              <p>Send a map pin with optional caption and copy send-location API samples.</p>
            </div>
            <span className="api-test-badge">
              <Icon icon="mdi:api" width={14} />
              POST · Location
            </span>
          </div>

          <MessageHeaderLayer
            getHeaderData={(id, apiKey) =>
              setData((prev) => ({ ...prev, client_id: id || null, token: apiKey }))
            }
          />

          <div className="api-test-grid">
            <form className="api-test-card" onSubmit={handleSubmit}>
              <div className="api-test-card-head">
                <h6>Request test</h6>
                <span>Geo pin</span>
              </div>
              <div className="api-test-card-body">
                <div className="api-test-note">
                  <Icon icon="mdi:information-outline" width={18} />
                  <span>Use it wisely — not recommended for bulk numbers from this tester.</span>
                </div>

                {alertMessage && (
                  <div className={`api-test-alert ${alertMessage.class}`}>{alertMessage.message}</div>
                )}

                <label className="api-test-label" htmlFor="loc-phone">
                  Phone
                </label>
                <input
                  id="loc-phone"
                  type="text"
                  className="form-control"
                  placeholder="923037900571"
                  value={data.mobile || ""}
                  onChange={(e) => setData({ ...data, mobile: e.target.value })}
                />
                <span className="api-test-help">Mobile number with country code, without +</span>

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="api-test-label" htmlFor="loc-lat">
                      Latitude
                    </label>
                    <input
                      id="loc-lat"
                      type="text"
                      className="form-control"
                      placeholder="24.8607"
                      value={data.latitude || ""}
                      onChange={(e) => setData({ ...data, latitude: e.target.value })}
                    />
                  </div>
                  <div className="col-6">
                    <label className="api-test-label" htmlFor="loc-lng">
                      Longitude
                    </label>
                    <input
                      id="loc-lng"
                      type="text"
                      className="form-control"
                      placeholder="67.0011"
                      value={data.longitude || ""}
                      onChange={(e) => setData({ ...data, longitude: e.target.value })}
                    />
                  </div>
                </div>

                <label className="api-test-label" htmlFor="loc-text">
                  Caption (optional)
                </label>
                <textarea
                  id="loc-text"
                  rows={4}
                  className="form-control"
                  placeholder="Type message here..."
                  value={data.text || ""}
                  onChange={(e) => setData({ ...data, text: e.target.value })}
                />

                <div className="api-test-actions">
                  <button type="submit" className="btn api-test-submit" disabled={spinner}>
                    {spinner ? "Sending…" : "Send Location"}
                  </button>
                </div>
              </div>
            </form>

            <CodeSamplesPanel kind="location" params={sampleParams} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationLayer;
